import { CircularProgress, Fade, LinearProgress, makeStyles } from "@material-ui/core";
import { differenceInSeconds } from "date-fns";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { KvixIcon } from "../../icons";
const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        paddingTop: "56.25%",
        overflow: "hidden",
        position: "relative",
        background: "#222",
        boxShadow: "0 6px 16px -8px rgba(0, 0, 0, 0.2)"
    },
    overlay: {
        pointerEvents: "none",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    image: {
        display: "block",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        "&:before": {
            content: "''",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
        }
    },
    linearProgress: {
        marginTop: "-4px",
        backgroundColor: theme.palette.common.black,
        "& .MuiLinearProgress-bar1Determinate": {
            borderRadius: "0px 5px 5px 0px"
        }
    },
    play: {
        padding: ({ size }) => (size === "large" ? 8 : 2),
        fontSize: ({ size }) => (size === "large" ? 50 : 28),
        color: theme.palette.common.white,
        background: theme.palette.primary.main,
        boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.3)",
        borderRadius: "50%",
        transition: "all 0.125s ease-out"
    }
}));
const useVideoThumbnail = (replay) => {
    const [uri, setUri] = useState(null);
    const [loading, setLoading] = useState(true);
    const video = useRef();
    const canvas = useRef();
    const resize = useCallback(() => {
        canvas.current.width = video.current.videoWidth;
        canvas.current.height = video.current.videoHeight;
    }, []);
    const capture = useCallback(() => {
        const context = canvas.current.getContext("2d");
        context.drawImage(video.current, 0, 0, video.current.videoWidth, video.current.videoHeight);
        const uri = canvas.current.toDataURL("image/jpeg");
        setUri(uri);
        setLoading(false);
    }, []);
    useEffect(() => {
        if (replay.thumbnailUrl) {
            setUri(replay.thumbnailUrl);
            setLoading(false);
            return;
        }
        const url = replay.mp4Url || replay.hlsUrl;
        setUri(null);
        setLoading(true);
        video.current = document.createElement("video");
        canvas.current = document.createElement("canvas");
        video.current.defaultMuted = true;
        video.current.muted = true;
        video.current.volume = 0;
        video.current.preload = "metadata";
        video.current.setAttribute("crossOrigin", "anonymous");
        video.current.setAttribute("playsInline", "");
        video.current.setAttribute("controls", "");
        video.current.addEventListener("loadedmetadata", resize);
        video.current.addEventListener("loadeddata", capture);
        video.current.src = url;
        video.current.currentTime = 3;
        video.current.load();
        return () => {
            var _a, _b, _c, _d;
            (_a = video.current) === null || _a === void 0 ? void 0 : _a.removeEventListener("loadedmetadata", resize);
            (_b = video.current) === null || _b === void 0 ? void 0 : _b.removeEventListener("loadeddata", capture);
            (_c = video.current) === null || _c === void 0 ? void 0 : _c.remove();
            (_d = canvas.current) === null || _d === void 0 ? void 0 : _d.remove();
            video.current = null;
            canvas.current = null;
        };
    }, [capture, replay.hlsUrl, replay.mp4Url, replay.thumbnailUrl, resize]);
    return [uri, loading];
};
export const ReplayListItemPoster = (props) => {
    const classes = useStyles(props);
    const [uri, loading] = useVideoThumbnail(props.replay);
    const getSessionDurationInSeconds = () => {
        if (props.replay.actualEndDate && props.replay.actualStartDate) {
            return differenceInSeconds(new Date(props.replay.actualEndDate), new Date(props.replay.actualStartDate));
        }
        else {
            return differenceInSeconds(new Date(props.replay.end), new Date(props.replay.start));
        }
    };
    const progress = (props.startTime / getSessionDurationInSeconds()) * 100;
    return (React.createElement("span", { className: classes.root },
        React.createElement(Fade, { in: !loading, timeout: 1000 },
            React.createElement("span", { className: classes.image, style: {
                    backgroundImage: `url(${uri})`,
                    backgroundSize: "cover"
                } })),
        React.createElement("span", { className: classes.overlay },
            React.createElement(KvixIcon.Play, { className: classes.play, color: "primary" })),
        React.createElement(Fade, { in: loading },
            React.createElement("span", { className: classes.overlay },
                React.createElement(CircularProgress, { size: 38, thickness: 4 }))),
        props.startTime && (React.createElement(LinearProgress, { className: classes.linearProgress, variant: "determinate", value: progress > 100 ? 100 : progress }))));
};
//# sourceMappingURL=ReplayListItemPoster.js.map